// External Dependencies
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  SparklesIcon,
  ArrowLeftIcon,
  PaperAirplaneIcon,
  TrashIcon,
  ClipboardIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Internal Dependencies
import { useLoading } from '../context/LoadingContext';
import { aiService } from '../services/aiService';

// Types
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface ChatSession {
  id: string;
  title: string;
  messages: Message[];
  lastUpdated: Date;
}

export default function Chat() {
  // State
  const [sessions, setSessions] = useState<ChatSession[]>(() => {
    const savedSessions = localStorage.getItem('chatSessions');
    return savedSessions ? JSON.parse(savedSessions) : [];
  });
  const [currentSessionId, setCurrentSessionId] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const { setLoading } = useLoading();
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout>();

  // Utils
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (date: Date | string) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Session Management
  const createNewSession = useCallback(() => {
    const newSession: ChatSession = {
      id: Date.now().toString(),
      title: 'New Chat',
      messages: [],
      lastUpdated: new Date(),
    };
    
    setSessions(prev => [newSession, ...prev]);
    setCurrentSessionId(newSession.id);
    setMessages([]);
  }, []);

  const switchSession = (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (session) {
      setCurrentSessionId(sessionId);
      setMessages(session.messages);
      setShowSessions(false);
    }
  };

  const clearChat = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updatedSessions = sessions.map(session => {
      if (session.id === sessionId) {
        return {
          ...session,
          messages: [],
          lastUpdated: new Date(),
        };
      }
      return session;
    });
    setSessions(updatedSessions);
    if (sessionId === currentSessionId) {
      setMessages([]);
    }
  };

  const deleteSession = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSessions = sessions.filter(s => s.id !== sessionId);
    setSessions(newSessions);
    
    if (sessionId === currentSessionId) {
      if (newSessions.length > 0) {
        switchSession(newSessions[0].id);
      } else {
        setCurrentSessionId('');
        setMessages([]);
      }
    }
  };

  const changeSessionName = (sessionId: string, newName: string) => {
    const updatedSessions = sessions.map(session => {
      if (session.id === sessionId) {
        return {
          ...session,
          title: newName,
          lastUpdated: new Date(),
        };
      }
      return session;
    });
    setSessions(updatedSessions);
  };

  // Save sessions to localStorage
  useEffect(() => {
    localStorage.setItem('chatSessions', JSON.stringify(sessions));
  }, [sessions]);

  // Load initial session
  useEffect(() => {
    const initializeChat = () => {
      if (sessions.length === 0) {
        createNewSession();
      } else {
        setCurrentSessionId(sessions[0].id);
        setMessages(sessions[0].messages);
      }
    };

    initializeChat();
  }, [sessions, createNewSession]);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    if (inputRef.current) {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = setTimeout(() => {
        const textarea = inputRef.current;
        if (textarea) {
          textarea.style.height = 'auto';
          const newHeight = Math.min(textarea.scrollHeight, 200);
          textarea.style.height = `${newHeight}px`;
        }
      }, 10);
    }
  }, []);

  // Event Handlers
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    // Update messages immediately for user's message
    const updatedMessages = [...messages, newMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsTyping(true);
    scrollToBottom();

    try {
      // Save the updated session
      const updatedSession = {
        ...sessions.find(s => s.id === currentSessionId)!,
        messages: updatedMessages,
        lastUpdated: new Date(),
      };
      const newSessions = sessions.map(s =>
        s.id === currentSessionId ? updatedSession : s
      );
      setSessions(newSessions);
      localStorage.setItem('chatSessions', JSON.stringify(newSessions));

      // Get AI response
      const response = await aiService.generateResponse(input.trim());
      
      // Add AI response
      const aiMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: response,
        timestamp: new Date(),
      };

      const finalMessages = [...updatedMessages, aiMessage];
      setMessages(finalMessages);

      // Update session with AI response
      const finalSession = {
        ...updatedSession,
        messages: finalMessages,
        lastUpdated: new Date(),
      };
      const finalSessions = sessions.map(s =>
        s.id === currentSessionId ? finalSession : s
      );
      setSessions(finalSessions);
      localStorage.setItem('chatSessions', JSON.stringify(finalSessions));
    } catch (error) {
      console.error('Error generating response:', error);
      // Show error message to user
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
      };
      setMessages([...updatedMessages, errorMessage]);
    } finally {
      setIsTyping(false);
      scrollToBottom();
    }
  };

  const copyToClipboard = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const renderCodeBlock = (code: string, language: string = 'typescript') => {
    const isCopied = code === copiedCode;
    
    return (
      <div className="relative group">
        <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => copyToClipboard(code)}
            className="p-2 rounded bg-gray-700 hover:bg-gray-600 text-gray-300"
            title={isCopied ? 'Copied!' : 'Copy code'}
          >
            {isCopied ? (
              <CheckIcon className="h-4 w-4 text-green-500" />
            ) : (
              <ClipboardIcon className="h-4 w-4" />
            )}
          </button>
        </div>
        <SyntaxHighlighter
          language={language}
          style={oneDark}
          customStyle={{
            padding: '1rem',
            borderRadius: '0.5rem',
            margin: '0.5rem 0',
            background: '#282c34',
          }}
          showLineNumbers={true}
          wrapLines={true}
        >
          {code}
        </SyntaxHighlighter>
      </div>
    );
  };

  const detectAndRenderCodeBlocks = (content: string) => {
    // Match code blocks with triple backticks and optional language
    const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = codeBlockRegex.exec(content)) !== null) {
      // Add text before code block
      if (match.index > lastIndex) {
        parts.push(content.slice(lastIndex, match.index));
      }

      const [, language = 'typescript', code] = match;
      parts.push(renderCodeBlock(code.trim(), language.toLowerCase()));
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last code block
    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }

    return parts.map((part, index) => 
      typeof part === 'string' ? (
        <span key={index} className="whitespace-pre-wrap">
          {part}
        </span>
      ) : (
        <React.Fragment key={index}>{part}</React.Fragment>
      )
    );
  };

  const renderMessageContent = (content: string) => {
    return detectAndRenderCodeBlocks(content);
  };

  // Effects
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [input, adjustTextareaHeight]);

  useEffect(() => {
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Initial loading
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [setLoading]);

  // Render Components
  const renderHeader = () => {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="inline-flex items-center text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </Link>
              <div className="flex items-center space-x-3">
                <div className="h-3 w-3 bg-purple-500 rounded-full animate-pulse" />
                <h1 className="text-xl font-bold text-white">Chat</h1>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={createNewSession}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-indigo-500/25"
              >
                <SparklesIcon className="w-5 h-5" />
                <span className="font-medium">New Chat</span>
              </button>
              <button
                onClick={() => setShowSessions(!showSessions)}
                className="inline-flex items-center px-3 py-2 text-gray-400 hover:text-white transition-colors"
                title={showSessions ? 'Hide History' : 'Show History'}
              >
                <ClipboardIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDateSeparator = (date: Date) => (
    <div className="flex items-center justify-center my-4">
      <div className="bg-gray-700/50 rounded-full px-3 py-1">
        <span className="text-xs text-gray-300">{formatDate(date)}</span>
      </div>
    </div>
  );

  const renderMessages = () => {
    let lastDate: string | null = null;
    
    return messages.map((message, index) => {
      const messageDate = new Date(message.timestamp).toDateString();
      const showDateSeparator = lastDate !== messageDate;
      lastDate = messageDate;

      return (
        <React.Fragment key={message.id}>
          {showDateSeparator && renderDateSeparator(message.timestamp)}
          <div
            className={`flex ${
              message.role === 'assistant' ? 'justify-start' : 'justify-end'
            } mb-4`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-4 ${
                message.role === 'assistant'
                  ? 'bg-gray-700 text-white'
                  : 'bg-blue-600 text-white'
              }`}
            >
              <div className="flex flex-col">
                <div className="whitespace-pre-wrap break-words">
                  {renderMessageContent(message.content)}
                </div>
                <div className={`text-xs mt-2 ${
                  message.role === 'assistant' ? 'text-gray-400' : 'text-blue-200'
                }`}>
                  {formatTime(message.timestamp)}
                </div>
              </div>
            </div>
          </div>
        </React.Fragment>
      );
    });
  };

  const renderLoadingAnimation = () => (
    <div className="flex justify-start">
      <div className="max-w-[80%] bg-gray-700 text-white rounded-lg p-4">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full opacity-75 animate-pulse"></div>
            <div className="relative bg-gray-800 rounded-full p-2">
              <SparklesIcon className="w-5 h-5 text-indigo-400 animate-spin" />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <div className="h-4 w-24 bg-gray-600 rounded animate-pulse"></div>
            </div>
            <div className="space-y-2">
              <div className="h-3 w-48 bg-gray-600 rounded animate-pulse"></div>
              <div className="h-3 w-32 bg-gray-600 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderChatInput = () => (
    <form onSubmit={handleSubmit} className="p-4 border-t border-gray-700 bg-gray-800/50 backdrop-blur-sm">
      <div className="max-w-4xl mx-auto flex items-end space-x-4">
        <div className="flex-1 min-w-0">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            placeholder="Type your message..."
            className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500 resize-none dark-scrollbar"
            rows={1}
          />
        </div>
        <button
          type="submit"
          disabled={!input.trim() || isTyping}
          className={`
            inline-flex items-center justify-center p-3 rounded-lg
            ${!input.trim() || isTyping
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
            }
            transition-colors duration-200
          `}
        >
          <PaperAirplaneIcon className="h-5 w-5" />
        </button>
      </div>
    </form>
  );

  return (
    <div className="flex flex-col min-h-screen bg-gray-900">
      {renderHeader()}
      {/* Sidebar */}
      <div 
        className={`fixed right-0 top-16 bottom-0 w-64 bg-gray-800 border-l border-gray-700 transform transition-transform duration-300 ease-in-out ${
          showSessions ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Chat History */}
        <div className="flex-1 h-full overflow-y-auto dark-scrollbar">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <ClipboardIcon className="w-5 h-5 text-indigo-400" />
                Chat History
              </h2>
              <span className="text-xs text-gray-400">{sessions.length} chats</span>
            </div>
            <div className="space-y-2">
              {sessions.map(session => (
                <div
                  key={session.id}
                  onClick={() => switchSession(session.id)}
                  className={`group p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                    session.id === currentSessionId
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                      : 'bg-gray-700/50 text-gray-200 hover:bg-gray-700/70'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <input
                        type="text"
                        value={session.title}
                        onChange={(e) => changeSessionName(session.id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className={`w-full bg-transparent border-none focus:ring-0 focus:outline-none ${
                          session.id === currentSessionId
                            ? 'text-white placeholder-white/70'
                            : 'text-gray-200 placeholder-gray-400'
                        }`}
                        placeholder="Chat Title"
                      />
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs opacity-70">
                          {formatDate(new Date(session.lastUpdated))}
                        </span>
                        <span className="text-xs opacity-70">•</span>
                        <span className="text-xs opacity-70">
                          {session.messages.length} messages
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => clearChat(session.id, e)}
                        className="p-1 rounded hover:bg-gray-600/50"
                        title="Clear messages"
                      >
                        <TrashIcon className="w-4 h-4 text-gray-300" />
                      </button>
                      <button
                        onClick={(e) => deleteSession(session.id, e)}
                        className="p-1 rounded hover:bg-red-500/20"
                        title="Delete chat"
                      >
                        <TrashIcon className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              {sessions.length === 0 && (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-700/50 mb-3">
                    <ClipboardIcon className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-gray-400 text-sm">No chat history yet</p>
                  <p className="text-gray-500 text-xs mt-1">Start a new chat to begin</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 mt-16">
        <div className="max-w-4xl mx-auto px-4 py-6">
          {messages.length === 0 ? (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full opacity-75 group-hover:opacity-100 blur"></div>
                <div className="relative bg-gray-900 rounded-full p-4">
                  <SparklesIcon className="w-12 h-12 text-indigo-400" />
                </div>
              </div>
              <div className="mt-6 text-center">
                <h3 className="text-xl font-semibold text-white mb-2">Welcome to AI Chat</h3>
                <p className="text-gray-400 mb-8 max-w-md">
                  Experience intelligent conversations powered by advanced AI. Start a new chat or explore our features.
                </p>
                <div className="flex flex-col gap-4 items-center">
                  <button
                    onClick={createNewSession}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-indigo-500/25"
                  >
                    <SparklesIcon className="w-5 h-5" />
                    <span className="font-medium">Start New Chat</span>
                  </button>
                  <Link
                    to="/generation"
                    className="inline-flex items-center gap-2 px-6 py-3 text-gray-400 hover:text-white transition-colors"
                  >
                    <SparklesIcon className="w-5 h-5" />
                    <span>Try AI Generation</span>
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto dark-scrollbar p-4 space-y-4">
              {renderMessages()}
              {isTyping && renderLoadingAnimation()}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      {renderChatInput()}
    </div>
  );
}
